<template>
	<div>
		<SectionCard collapsible>
			<template #title>
				{{ getCategoryName(cookieCategory) }}
				<Chip
					v-if="collectionRow.isMspaCategory"
					class="ml-2 prussian-blue white--text rounded-pill"
				>
					MSPA Enabled
				</Chip>
			</template>
			<template #subtitle>
				<div
					v-dompurify-html="getCategoryDisplayText(cookieCategory)"
				/>
			</template>
			<template #body>
				<v-row
					v-if="userCanCreateUpdate"
					dense
				>
					<v-col cols="6">
						<Dropdown
							ref="dropdownValidationProvider"
							v-model="selectedScriptIds"
							:search-input.sync="searchInput"
							:label="`${getCategoryName(cookieCategory)} Services *`"
							multiple
							select-all
							searchable
							chips
							:rules="validateGtmConsentTypes"
							:items="availableCookieScriptItems"
							:html-error-message="true"
							@change="updateSelectAllChecked"
						>
							<template
								v-if="showSelectAll"
								#prepend-item
							>
								<v-list-item
									dense
									@click="toggleSelectAll"
								>
									<v-checkbox
										v-model="selectAllChecked"
										dense
										@click="toggleSelectAll"
									/>
									<v-list-item-title class="pb-2">
										Select All
									</v-list-item-title>
								</v-list-item>
								<v-divider />
							</template>
						</Dropdown>
					</v-col>
					<v-col cols="3">
						<PrimaryActionButton
							:disabled="!selectedScriptIds.length || hasDropdownErrors"
							@click="addScripts"
						>
							Add
						</PrimaryActionButton>
					</v-col>
					<v-col
						class="d-flex justify-end"
						cols="3"
					>
						<TextButton
							:disabled="cookieCollectionTemplate.brandId === null || !requiresValidation"
							@click="navigateToCreateService"
						>
							<v-icon>
								mdi-plus
							</v-icon>
							Create Service
						</TextButton>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="12">
						<ValidationProvider
							ref="cookieScriptValidationProvider"
							#default="{ errors }"
							v-model="categoryCookieScripts"
							:rules="requiresValidation ? 'atLeastOneCookieScript' : ''"
							:vid="validationId"
							tag="div"
						>
							<DataTable
								:headers="tableHeaders"
								:items="categoryCookieScripts"
							>
								<template #item.cookieScriptName="{ item }">
									{{ getCookieScriptName(item) }}
									<Chip
										v-if="item.channelGuid"
										class="ml-2 prussian-blue white--text rounded-pill"
									>
										System Default
									</Chip>
								</template>
								<template #item.isRunFirst="{ item }">
									<v-icon small>
										{{ getIsRunFirst(item) ? 'mdi-check' : 'mdi-close' }}
									</v-icon>
								</template>
								<template #item.gpcEnabled="{ item }">
									<v-icon small>
										{{ getGpcEnabled(item) ? 'mdi-check' : 'mdi-close' }}
									</v-icon>
								</template>
								<template #item.type="{ item }">
									{{ getCookieScriptType(item) }}
								</template>
								<template #item.displayOrder="{ index }">
									{{ index + 1 }}
								</template>
								<template
									v-if="userCanCreateUpdate"
									#item.action="{ item, index }"
								>
									<div class="d-flex align-center justify-end">
										<!-- Move Up/Down buttons stay outside the menu -->
										<IconButton
											v-if="index !== 0"
											@click="moveScriptUp(index, item)"
										>
											mdi-arrow-up
										</IconButton>
										<IconButton
											v-if="index !== categoryCookieScripts.length - 1"
											@click="moveScriptDown(index, item)"
										>
											mdi-arrow-down
										</IconButton>

										<!-- Ellipsis menu for other actions -->
										<v-menu offset-y>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item
													v-if="collectionRow.isMspaCategory"
													@click="openMspa(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Map MSPA Section
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="!item.channelGuid"
													@click="editCookieScript(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Edit Service
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-else
													@click="editCookieScript(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															View Service
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													@click="deleteScript(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Delete Service
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</div>
								</template>
							</DataTable>
							<span
								v-if="errors.length"
								class="syrenis-red--text"
							>
								{{ errors[0] }}
							</span>
						</ValidationProvider>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
		<CookieMspaScriptModal
			v-if="mspaModalOpen"
			:service="selectedService"
			:taken-by="takenByMap"
			:user-full-permissions="userCanCreateUpdate"
			@save="saveMspa"
			@close="mspaModalOpen = false"
		/>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { extend, ValidationProvider } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import TextButton from '../../../../../../../shared/components/text-button.vue'
import Chip from '../../../../../../../shared/components/chip.vue'
import IconButton from '../../../../../../../shared/components/icon-button.vue'
import CookieMspaScriptModal from '../cookie-scripts/cookie-mspa-script-modal.vue'

extend('atLeastOneCookieScript', {
	...required,
	validate (value) {
		const isValid = value && value.length > 0
		return isValid
	},
	message: 'Please select at least one service.'
})

export default {
	components: {
		SectionCard,
		TextButton,
		Dropdown,
		PrimaryActionButton,
		DataTable,
		IconButton,
		ValidationProvider,
		Chip,
		CookieMspaScriptModal
	},
	props: {
		cookieCollectionTemplate: Object,
		cookieCategoryScripts: Array,
		cookieCategories: Array,
		transactionTypes: Array,
		securityLevels: Array,
		channelDeviceCopyActions: Array,
		allCookieScripts: Array,
		cookieCategory: Object,
		userCanCreateUpdate: Boolean,
		serviceJustAdded: {
			type: Boolean,
			default: false
		},
		gtmConsentTypes: {
			type: Array,
			default: () => []
		},
		gtmScriptsInUse: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			cookieScriptToEdit: null,
			selectedScriptIds: [],
			selectAllChecked: false,
			searchInput: null,
			mspaModalOpen: false,
			selectedService: null,
			ignoreOnLoadScriptsValidation: true,
			hasDropdownErrors: false
		}
	},
	computed: {
		availableCookieScriptItems () {
			const existingCookieScripts = this.categoryCookieScripts.map(({ id }) => id)
			return this.cookieCategoryScripts.filter(({ value }) => !existingCookieScripts.includes(value)).sort((a, b) => a.text.localeCompare(b.text))
		},
		requiresValidation () {
			return !this.cookieCategory?.channelGuid
		},
		validationId () {
			const key = this.cookieCategory?.categoryId ?? this._uid
			return `cookie-category-${key}-scripts`
		},
		tableHeaders () {
			return [
				{ text: 'ID', value: 'id', width: '10%' },
				{ text: 'Service Name', value: 'cookieScriptName', width: '30%' },
				{ text: 'Run First?', value: 'isRunFirst', width: '15%' },
				{ text: 'GPC', value: 'gpcEnabled', width: '15%' },
				{ text: 'Type', value: 'type', width: '15%' },
				{ text: 'Display Order', value: 'displayOrder', width: '15%' },
				{ text: 'Action', value: 'action', width: '120px', align: 'right', sortable: false }
			]
		},
		takenByMap () {
			const map = {}
			// Check ALL scripts in the collection template, not just current category
			this.cookieCollectionTemplate.cookieScripts.forEach(row => {
				const full = this.allCookieScripts.find(s => s.id === row.id)
				;(row.mspaElementIds || []).forEach(elemId => {
					map[elemId] = {
						scriptId: row.id,
						serviceName: full ? `${full.cookieScriptName} – ${full.brandName}` : row.id
					}
				})
			})
			return map
		},
		collectionRow () {
			const cat = this.cookieCategory
			if (!cat) return {}
			return (
				this.cookieCollectionTemplate.cookieCategories.find(c => c.id === cat.id) || {}
			)
		},
		mspaToggleVisible () {
			return !this.cookieCategory?.isStrictlyCategory
		},
		categoryCookieScripts () {
			const result = this.cookieCollectionTemplate?.cookieScripts?.filter(script => script?.categoryId === this.cookieCategory?.categoryId)
			return result
		},
		strictlyScripts () {
			const scripts = this.cookieCollectionTemplate.cookieScripts.map(script => ({
				...script,
				isStrictlyNecessary: this.strictlyNecessaryScript(script.id)
			})).filter(({ isStrictlyNecessary }) => isStrictlyNecessary)
			return scripts
		},
		showSelectAll () {
			return this.availableCookieScriptItems.length > 1
		},
		validateGtmConsentTypes () {
			return {
				validateGtmConsentTypesInCollection: {
					gtmConsentTypes: this.gtmConsentTypes,
					allScripts: this.allCookieScripts,
					inUse: this.gtmScriptsInUse
				}
			}
		}
	},
	watch: {
		async categoryCookieScripts (newVal, oldVal) {
			// Skip validation only during the very first load when the component is still initializing
			if (this.ignoreOnLoadScriptsValidation && !oldVal) {
				return
			}
			// Always run validation for actual changes (when oldVal exists)
			this.validateCategoryScripts()
		},
		serviceJustAdded (newVal) {
			if (newVal) {
				// A service was just added, clear the ignore flag so validation runs properly
				this.ignoreOnLoadScriptsValidation = false
			}
		},
		cookieCollectionTemplate: {
			handler (newVal) {
				if (newVal && newVal.cookieScripts) {
					// Run validation when the collection template is loaded
					this.$nextTick(() => {
						if (!this.ignoreOnLoadScriptsValidation) {
							this.validateCategoryScripts()
						}
					})
				}
			},
			deep: true,
			immediate: true
		}
	},
	created () {
		// Check if there's a new cookie script ID from the manage-service page (only for direct route access)
		if (!this.$parent.accessedVia && this.$route.name !== 'manage-cookie-collection-template') {
			const newScriptId = sessionStorage.getItem('newCookieScriptId')
			if (newScriptId && this.cookieCategory) {
				// Clear it first to prevent repeated additions
				sessionStorage.removeItem('newCookieScriptId')
				this.addNewScriptToCollection(parseInt(newScriptId))
				this.$emit('load-cookie-scripts')
			}
		}
	},
	mounted () {
		// Run validation after component is mounted and all computed properties are available
		this.$nextTick(() => {
			// Set the flag to false first, then run validation
			this.ignoreOnLoadScriptsValidation = false
			// Always run initial validation regardless of the ignore flag
			this.validateCategoryScripts()
			// Also run validation again after a short delay to catch any async data loading
			setTimeout(() => {
				this.validateCategoryScripts()
			}, 100)
		})
	},
	methods: {
		addScripts () {
			this.ignoreOnLoadScriptsValidation = false
			const selectedScriptIds = this.selectedScriptIds.map(script => {
				// Find the full script data to preserve channelGuid (cassie default) if it exists
				const fullScript = this.allCookieScripts.find(({ id }) => id === script)
				const scriptData = {
					id: script,
					categoryId: this.cookieCategory.categoryId
				}
				if (fullScript?.channelGuid) {
					scriptData.channelGuid = fullScript.channelGuid
				}
				return scriptData
			})
			this.$emit('update:cookieScripts', [
				...this.cookieCollectionTemplate.cookieScripts,
				...selectedScriptIds
			])
			this.selectAllChecked = false
			this.searchInput = null
			this.selectAllChecked = false
			// solves issue where we try to assign run order before the cookie scripts have been emitted
			setTimeout(() => {
				selectedScriptIds.forEach(script => {
					this.assignRunOrder(script)
				})
				this.validateCategoryScripts()
			}, 0)
		},
		validateCategoryScripts () {
			if (this.$refs.cookieScriptValidationProvider) {
				this.$refs.cookieScriptValidationProvider.validate(this.categoryCookieScripts)
			}
		},
		addNewScriptToCollection (newScriptId) {
			if (!this.cookieCategory) {
				console.error('Cookie category is not available')
				return
			}
			const newScript = {
				id: newScriptId,
				categoryId: this.cookieCategory.categoryId
			}
			this.$emit('update:cookieScripts', [
				...this.cookieCollectionTemplate.cookieScripts,
				{
					id: newScriptId,
					categoryId: this.cookieCategory.categoryId
				}
			])
			this.assignRunOrder(newScript)
		},
		updateMspaFlag (val) {
			const rows = this.cookieCollectionTemplate.cookieCategories.map(row => ({
				...row,
				isMspaCategory: row.id === this.cookieCategory.id
					? val
					: false
			}))
			this.$emit('update:cookieCategories', rows)
		},
		getCategoryId (category) {
			return category?.categoryId
		},
		getCategoryName (category) {
			return category?.categoryName
		},
		getCategoryDisplayText (category) {
			return category?.displayText
		},
		getCookieScriptName ({ id }) {
			const matchedScript = this.allCookieScripts.find(script => script.id === id)
			return `${matchedScript?.cookieScriptName} - ${matchedScript?.brandName}`
		},
		getIsRunFirst ({ id }) {
			return this.allCookieScripts.find(script => script.id === id)?.isRunFirst
		},
		getGpcEnabled ({ id }) {
			return this.allCookieScripts.find(script => script.id === id)?.gpcEnabled
		},
		getCookieScriptType ({ id }) {
			return this.allCookieScripts.find(script => script.id === id)?.cookieScriptType
		},
		openMspa (row) {
			const full = this.allCookieScripts.find(s => s.id === row.id)
			this.selectedService = {
				id: row.id,
				cookieScriptName: full.cookieScriptName,
				brandName: full.brandName,
				mspaElementIds: Array.isArray(row.mspaElementIds)	? [...row.mspaElementIds]	: []
			}
			this.mspaModalOpen = true
		},
		saveMspa ({ serviceId, mspaElementIds }) {
			const list = [...this.cookieCollectionTemplate.cookieScripts]
			const idx = list.findIndex(s => s.id === serviceId)
			if (idx > -1) {
				list[idx] = { ...list[idx], mspaElementIds }
				this.$emit('update:cookieScripts', list)
			}
		},
		moveScriptDown (index, { id: scriptId }) {
			const cookieScriptCategoryId = this.allCookieScripts.find(({ id }) => id === scriptId)?.cookieCategoryId
			this.move(this.categoryCookieScripts, index, index + 1, cookieScriptCategoryId)
		},
		moveScriptUp (index, { id: scriptId }) {
			const cookieScriptCategoryId = this.allCookieScripts.find(({ id }) => id === scriptId)?.cookieCategoryId
			this.move(this.categoryCookieScripts, index, index - 1, cookieScriptCategoryId)
		},
		applySort (array) {
			this.$emit('update:cookieScripts', array)
		},
		move (array, from, to, cookieScriptCategoryId) {
			// find item category id and + 1 the index, then find the next item with that category id and - 1 the index
			const cookieScriptsNotInSelectedCategory = this.cookieCollectionTemplate.cookieScripts.filter(({ categoryId }) => categoryId !== cookieScriptCategoryId)
			const arrayCopy = JSON.parse(JSON.stringify(array))
			arrayCopy.splice(to, 0, arrayCopy.splice(from, 1)[0])
			return this.applySort([
				...arrayCopy,
				...cookieScriptsNotInSelectedCategory
			])
		},
		assignRunOrder (script) {
			const runOrderScripts = this.cookieCollectionTemplate.runOrderScripts
			let insertAtIndex = 0
			if (this.strictlyNecessaryScript(script.id) && this.runFirstScript(script.id)) {
				runOrderScripts.splice(insertAtIndex, 0, script)
				this.$emit('update:runOrderScripts', runOrderScripts)
			} else if (this.strictlyNecessaryScript(script.id) && !this.runFirstScript(script.id)) {
				insertAtIndex = this.strictlyScripts.length
				runOrderScripts.splice(insertAtIndex, 0, script)
				this.$emit('update:runOrderScripts', runOrderScripts)
			} else if (!this.strictlyNecessaryScript(script.id) && this.runFirstScript(script.id)) {
				insertAtIndex = this.strictlyScripts.length
				runOrderScripts.splice(insertAtIndex, 0, script)
				this.$emit('update:runOrderScripts', runOrderScripts)
			} else {
				runOrderScripts.push(script)
				this.$emit('update:runOrderScripts', runOrderScripts)
			}
			this.selectedScriptIds = []
		},
		navigateToCreateService () {
			// Always emit the event to parent collection template
			this.$emit('show-manage-service', this.getCategoryId(this.cookieCategory))
		},
		deleteScript ({ id }) {
			const deletedScriptIndex = this.cookieCollectionTemplate.cookieScripts.findIndex(script => script.id === id)
			const cookieScripts = this.cookieCollectionTemplate.cookieScripts
			const runOrderScripts = this.cookieCollectionTemplate.runOrderScripts
			const deletedRunOrderScriptIndex = this.cookieCollectionTemplate.runOrderScripts.findIndex(script => script.id === id)
			this.$delete(cookieScripts, deletedScriptIndex)
			this.$delete(runOrderScripts, deletedRunOrderScriptIndex)
			this.$emit('update:cookieScripts', cookieScripts)
			this.$emit('update:runOrderScripts', runOrderScripts)
			// Ensure validation runs after deletion
			this.$nextTick(() => {
				this.validateCategoryScripts()
			})
		},
		strictlyNecessaryScript (scriptId) {
			return this.allCookieScripts.find(({ id }) => id === scriptId)?.cookieScriptType === 'Essential'
		},
		runFirstScript (scriptId) {
			return this.allCookieScripts.find(({ id }) => id === scriptId)?.isRunFirst
		},
		toggleSelectAll () {
			if (this.selectAllChecked) {
				this.selectAllChecked = false
				this.selectedScriptIds = []
			} else {
				this.selectAllChecked = true
				if (this.searchInput) {
					this.selectedScriptIds = this.availableCookieScriptItems.filter(script => script.text.toLowerCase().includes(this.searchInput.toLowerCase())).map(script => script.value)
				} else {
					this.selectedScriptIds = this.availableCookieScriptItems.map(script => {
						return script.value
					})
				}
			}
		},
		updateSelectAllChecked () {
			const filteredItems = this.searchInput
				? this.availableCookieScriptItems.filter(script => script.text.toLowerCase().includes(this.searchInput.toLowerCase()))
				: this.availableCookieScriptItems
			this.selectAllChecked = this.selectedScriptIds.length === filteredItems.length
			// Validate dropdown and update error state
			this.$nextTick(async () => {
				if (this.$refs.dropdownValidationProvider) {
					const result = await this.$refs.dropdownValidationProvider.validate()
					this.hasDropdownErrors = !result.valid
				}
			})
		},
		async editCookieScript (script) {
			// Get the cookieScriptChannelId from the full script data
			const cookieScriptChannelId = this.allCookieScripts.find(({ id }) => id === script.id)?.cookieScriptChannelId
			// Emit event to parent collection template with the channel ID so ManageService can load full data
			this.$emit('show-manage-service', this.getCategoryId(this.cookieCategory), null, cookieScriptChannelId)
		}
	}
}
</script>
